import React, {
  ComponentType,
  FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Calendar, momentLocalizer, SlotInfo } from 'react-big-calendar';
import dayjs from 'dayjs';
import moment from 'moment';
import withDragAndDrop, {
  DragFromOutsideItemArgs,
  EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';

import './CalendarTimeline.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useRecoilValue } from 'recoil';

import { EventBlock } from './EventBlock';
import { RBCEvent } from './types';
import { CustomTimeGutter } from './CustomTimeGutter';
import {
  PeakDipPhase,
  PeakDipStartEndSet,
  selectedDateAtom,
  useAssistantChat,
  useAuthContext,
  useCalendarContext,
  useCircadianContext,
  useCreateEventMenuContext,
  useEventBlock,
  useEventDetailsMenuContext,
  suggestAllAIPlanStateAtom,
} from '../../../data-access';
import { CALENDAR_STEP_MIN, SCROLL_OFFSET_HOURS, zoneColorForPeaksDipsPhase } from './constants';
import { getCssVariable } from '../../../utils';
import { ActivitySaveMenu, EditEventMenu, TemporaryEventMenu } from '../EventMenu';
import { AISuggestedEventModal } from '../AISuggestedEventModal';
import {
  buildNapSummaryRichContextFromActivityEvent,
  buildWorkoutSummaryRichContextFromActivityEvent,
} from '../../../helpers';
import {
  getEventStyles,
  transfomrAutoScheduledEvents,
  transformCreatingEventToRBCEvent,
  transformActivityEventsToRBCEvents,
  getRoundUpDateTime,
  getRoundDownDateTime,
  transformChatSuggesteBlockToRBCEvents,
  getOverridingEventIds,
} from './helpers';
import { translateReplannedScheduledActionToCalendarEvent } from '../../../helpers';
import { getScheduleId } from '../../Scheduler/AllPlanScheduler/helpers';
import { useGeneralSettings } from '../../../hooks/useGeneralSettings';
import {
  useAIScheduler,
  useDnDTaskToCalendar,
  useMetricsActivitiesInCalendar,
} from '../../../hooks';
import { AISchedulerFeedbackDialog, AISchedulerResultModal } from '../../Scheduler';
import { CalendarEvent } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

interface CalendarTimelineProps {
  events: RBCEvent[];
  onEventDrop: (data: EventInteractionArgs<object>) => void;
  onEventResize: (data: EventInteractionArgs<object>) => void;
  className?: string;
  date?: Date;
  minTime?: Date;
  maxTime?: Date;
  scrollToTime?: Date;
}

export type CalendarTimelineBackgroundEvent = Partial<RBCEvent> & {
  start: Date;
  end: Date;
  color: string;
  resourceId?: string;
};

interface MemoizedCalendarProps extends CalendarTimelineProps {
  key?: string;
  defaultDraggedEvent?: CalendarEvent;
  onDropEventFromOutside: (data: DragFromOutsideItemArgs) => void;
  onSelectEvent: (event: RBCEvent, nativeEvent?: Event | null) => void;
  onSelectSlot: (slot: SlotInfo) => void;
  onRemoveTemporalSuggestedEvent?: (eventId: string) => void;
  disableScroll?: boolean;
  scrollToTime?: Date;
  disableInteraction?: boolean;
  peaksDipsBoundariesOverride?: PeakDipStartEndSet | null;
  backgroundEventsOverride?: CalendarTimelineBackgroundEvent[];
  canInteractWithEvent?: (event: RBCEvent) => boolean;
  canSelectSlot?: (slot: SlotInfo) => boolean;
  resources?: Record<string, unknown>[];
  resourceHeader?: ComponentType<{
    index: number;
    label: React.ReactNode;
    resource: Record<string, unknown>;
  }>;
  resourceIdAccessor?: string;
  resourceTitleAccessor?: string;
}

const localizer = momentLocalizer(moment); // dayjsLocalizer is so slow.
const DnDCalendar = withDragAndDrop(Calendar);
const primaryColor = getCssVariable('--color-primary') || '#680ddb';
const grayBase = getCssVariable('--color-gray-base') || '#b5b5bc';
const calendarWidth = 400; //--calendar-width

const CustomTimeGutterWrapper = memo(
  ({
    children,
    disabled = false,
    peaksDipsBoundariesOverride,
  }: React.PropsWithChildren<{
    disabled?: boolean;
    peaksDipsBoundariesOverride?: PeakDipStartEndSet | null;
  }>) => {
    const { peaksDipsBoundaries } = useCircadianContext();
    const resolvedPeaksDipsBoundaries =
      peaksDipsBoundariesOverride === undefined
        ? peaksDipsBoundaries
        : peaksDipsBoundariesOverride || undefined;

    return (
      <CustomTimeGutter peaksDipsBoundaries={resolvedPeaksDipsBoundaries} disabled={disabled}>
        {children}
      </CustomTimeGutter>
    );
  }
);

export const MemoizedCalendar = React.memo(
  forwardRef<HTMLDivElement, MemoizedCalendarProps>(
    (
      {
        key,
        onRemoveTemporalSuggestedEvent = () => void 0,
        events,
        onEventDrop,
        onEventResize,
        defaultDraggedEvent,
        onDropEventFromOutside,
        onSelectEvent,
        onSelectSlot,
        className,
        date,
        disableScroll = false,
        scrollToTime,
        disableInteraction = false,
        peaksDipsBoundariesOverride,
        backgroundEventsOverride,
        canInteractWithEvent,
        canSelectSlot,
        resources,
        resourceHeader,
        resourceIdAccessor = 'id',
        resourceTitleAccessor = 'title',
      },
      ref
    ) => {
      const { peaksDipsBoundaries } = useCircadianContext();
      const { t } = useTranslation();
      const selectedDate = useRecoilValue(selectedDateAtom);
      const calendarDate = date || selectedDate.toDate();
      const {
        generalSettings: { timeFormat },
      } = useGeneralSettings();
      const { checkCalendarHassAccessToModifyEvent } = useCalendarContext();
      const {
        shouldShowTaskEvent,
        handleTaskComplete,
        handleRoutineComplete,
        getTargetTaskFromEvent,
        handleAcceptSuggestedTaskEvent,
        handleDiscardSuggestedTaskEvent,
      } = useEventBlock({ onRemoveTemporalSuggestedEvent });
      const [draggedEvent, setDraggedEvent] = useState<Partial<RBCEvent> | null>(null);

      const selectedTimeFormat = timeFormat === '12h' ? 'h a' : 'HH';
      const calendarMessages = useMemo(
        () => ({
          allDay: t('calendar.common.allDay'),
          date: t('calendar.common.date'),
          time: t('calendar.common.time'),
          event: t('calendar.common.event'),
          week: t('calendar.views.week'),
          day: t('calendar.views.day'),
          month: t('calendar.views.month'),
          previous: t('calendar.header.previousPeriod'),
          next: t('calendar.header.nextPeriod'),
          yesterday: t('calendar.common.yesterday'),
          tomorrow: t('calendar.common.tomorrow'),
          today: t('calendar.common.today'),
          agenda: t('calendar.views.agenda'),
          noEventsInRange: t('calendar.weekMonth.noEventsInRange'),
          showMore: (total: number) => t('calendar.weekMonth.showMore', { count: total }),
        }),
        [t]
      );

      // Initialize dragged event
      useEffect(() => {
        if (!defaultDraggedEvent) {
          return;
        }

        setDraggedEvent({
          title: defaultDraggedEvent.summary,
          eventId:
            defaultDraggedEvent.taskFrom?.taskId || defaultDraggedEvent.eventId || 'dragging',
          color: defaultDraggedEvent.color || primaryColor,
          categories: defaultDraggedEvent.categories,
          taskFrom: defaultDraggedEvent.taskFrom,
          scheduleFrom: defaultDraggedEvent.scheduleFrom,
        });
      }, [JSON.stringify(defaultDraggedEvent)]);

      const backgroundEvents = useMemo(() => {
        if (backgroundEventsOverride) {
          return backgroundEventsOverride;
        }

        const resolvedPeaksDipsBoundaries =
          peaksDipsBoundariesOverride === undefined
            ? peaksDipsBoundaries
            : peaksDipsBoundariesOverride || undefined;

        return resolvedPeaksDipsBoundaries
          ? Object.entries(resolvedPeaksDipsBoundaries)
              .filter(([phase, { start, end }]) => phase !== 'undefined' && !!start && !!end)
              .map(([phase, { start, end }]) => ({
                start: getRoundUpDateTime(dayjs(start)).toDate(),
                end: getRoundDownDateTime(dayjs(end)).toDate(),
                color: zoneColorForPeaksDipsPhase[phase as PeakDipPhase],
              }))
          : [];
      }, [backgroundEventsOverride, peaksDipsBoundaries, peaksDipsBoundariesOverride]);

      const handleDropFromOutside = (data: DragFromOutsideItemArgs) => {
        onDropEventFromOutside(data);
        setDraggedEvent(null);
      };

      const dragFromOutsideItem = useCallback(() => draggedEvent, [draggedEvent]);

      return (
        <div
          ref={ref}
          className={clsx('calendar-timeline__container', {
            'calendar-timeline__container--disable-scroll': disableScroll,
          })}
        >
          {/* @ts-ignore */}
          <DnDCalendar
            // key={key}
            date={calendarDate}
            onNavigate={() => {}} // Added empty onNavigate handler to fix warning
            defaultView="day"
            events={events}
            resources={resources}
            resourceIdAccessor={resourceIdAccessor}
            resourceTitleAccessor={resourceTitleAccessor}
            localizer={localizer}
            messages={calendarMessages}
            onEventDrop={onEventDrop}
            onSelectEvent={(event, e) => {
              const rbcEvent = event as RBCEvent;

              if (canInteractWithEvent && !canInteractWithEvent(rbcEvent)) {
                return;
              }

              onSelectEvent(rbcEvent, e?.nativeEvent ?? null);
            }}
            onEventResize={onEventResize}
            //@ts-ignore
            dragFromOutsideItem={dragFromOutsideItem}
            draggableAccessor={(event: RBCEvent) => {
              if (disableInteraction || event.isTemporalSuggested || !event.calendarId) {
                return false;
              }

              if (canInteractWithEvent && !canInteractWithEvent(event)) {
                return false;
              }

              return checkCalendarHassAccessToModifyEvent(event.calendarId);
            }}
            resizableAccessor={(event: RBCEvent) => {
              if (disableInteraction || event.isTemporalSuggested || !event.calendarId) {
                return false;
              }

              if (canInteractWithEvent && !canInteractWithEvent(event)) {
                return false;
              }

              return checkCalendarHassAccessToModifyEvent(event.calendarId);
            }}
            onDropFromOutside={handleDropFromOutside}
            onDragOver={(e) => e.preventDefault()}
            onSelectSlot={(slot) => {
              if (canSelectSlot && !canSelectSlot(slot)) {
                return;
              }

              onSelectSlot(slot);
            }}
            resizable={!disableInteraction}
            selectable={!disableInteraction}
            showMultiDayTimes
            step={CALENDAR_STEP_MIN}
            dayLayoutAlgorithm="no-overlap"
            scrollToTime={scrollToTime}
            formats={{
              timeGutterFormat: (date) => {
                if (dayjs(date).minute() === 0) {
                  return dayjs(date).format(selectedTimeFormat);
                }
              },
            }}
            backgroundEvents={backgroundEvents}
            components={{
              // @ts-ignore
              timeGutterWrapper: (props) => (
                <CustomTimeGutterWrapper
                  {...props}
                  disabled={disableInteraction}
                  peaksDipsBoundariesOverride={peaksDipsBoundariesOverride}
                />
              ),
              ...(resourceHeader ? { resourceHeader } : {}),
              event: (event) => {
                const rbcEvent = event.event as RBCEvent;
                const isEventInteractive = !canInteractWithEvent || canInteractWithEvent(rbcEvent);

                return shouldShowTaskEvent(rbcEvent) ? (
                  <EventBlock
                    event={rbcEvent}
                    onTaskStatusChange={isEventInteractive ? handleTaskComplete : undefined}
                    onRoutineStatusChange={isEventInteractive ? handleRoutineComplete : undefined}
                    taskLinkedToEvent={getTargetTaskFromEvent(rbcEvent)}
                    onAcceptSuggestedTask={
                      isEventInteractive ? handleAcceptSuggestedTaskEvent : undefined
                    }
                    onDiscardSuggestedTask={
                      isEventInteractive ? handleDiscardSuggestedTaskEvent : undefined
                    }
                  />
                ) : null;
              },
            }}
            toolbar={false}
            eventPropGetter={(rbcEvent: RBCEvent) => getEventStyles(rbcEvent, false)}
            className={clsx('calendar-timeline', className)}
          />
        </div>
      );
    }
  ),
  (prevProps, nextProps) => {
    const propsEqual = JSON.stringify(prevProps) === JSON.stringify(nextProps);
    const defaultDraggedEventEqual =
      JSON.stringify(prevProps.defaultDraggedEvent) ===
      JSON.stringify(nextProps.defaultDraggedEvent);
    // Return true to skip re-render only if both are equal
    return propsEqual && defaultDraggedEventEqual;
  }
);

export const CalendarTimeline: FC<CalendarTimelineProps> = memo(
  ({ events, onEventDrop, onEventResize, className, date, minTime, maxTime, scrollToTime }) => {
    const { t } = useTranslation();
    const { creatingEvent, handleSelectNewEvent } = useCreateEventMenuContext();
    const { temporalSuggestedEvents, removeTemporalSuggestedEvent } = useAssistantChat();
    const suggestAllAIPlanState = useRecoilValue(suggestAllAIPlanStateAtom);
    const { visibleMenuMode, selectedEvent, handleSelectEvent, clearSelectedEvent } =
      useEventDetailsMenuContext();
    const { mainCalendar } = useCalendarContext();
    const currentScrollPosition = useRef<number>(0);

    const {
      draggedNewEvent: defaultDraggedEvent,
      dropTaskOnCalendar,
      handleDropEventFromOutside: handleDropEventFromOutsideApi,
    } = useDnDTaskToCalendar();
    const { user } = useAuthContext();
    const [rerenderTrigger, setRerenderTrigger] = useState(0); // New state to trigger rerenders

    const {
      aiScheduledActionsEvents,
      aiFeedbackDialogVisible,
      handleFeedbackDialogClose,
      handleConfirmDiscard,
    } = useAIScheduler();
    const {
      activityEvents,
      selectedActivityEvent,
      clearSelectedActivityEvent,
      handleDiscardActivityEvent,
      handleSaveActivityEvent,
    } = useMetricsActivitiesInCalendar();

    const eventWithCreatingOne = useMemo(() => {
      const visibleEvents = events;
      const transformedAIScheduledEvents = transfomrAutoScheduledEvents(
        aiScheduledActionsEvents,
        mainCalendar?.color
      );
      const transformedActivityEvents = transformActivityEventsToRBCEvents(activityEvents);
      const transformedChatEvents = transformChatSuggesteBlockToRBCEvents(temporalSuggestedEvents);
    const transformedAllPlanEvents = !suggestAllAIPlanState.isLoading
      ? suggestAllAIPlanState.schedules
          .map((schedule, index) => {
            const scheduleId = getScheduleId(schedule, index);
            if (
              suggestAllAIPlanState.acceptedIds.includes(scheduleId) ||
              suggestAllAIPlanState.discardedIds.includes(scheduleId)
            ) {
              return null;
            }

            const calendarEvent = translateReplannedScheduledActionToCalendarEvent({
              ...schedule,
              id: scheduleId,
            });

            return {
              ...calendarEvent,
              start: new Date(calendarEvent.start.date),
              end: new Date(calendarEvent.end.date),
              title: calendarEvent.summary,
              allDay: false,
              calendarId: mainCalendar?.calendarId,
              color: mainCalendar?.color,
              isReplanPreview: true,
              eventId: `all_plan_${scheduleId}`,
            } as RBCEvent;
          })
          .filter((event): event is RBCEvent => !!event)
      : [];
    // Prefer all-plan previews over chat temporal overlays for the same
    // action_id (scheduler_chat dual-write would otherwise show both).
    const allPlanActionIds = new Set(
      transformedAllPlanEvents
        .map((event) => event.eventId?.replace(/^all_plan_/, ''))
        .filter((id): id is string => !!id)
    );
    const chatEventsWithoutAllPlanDupes = transformedChatEvents.filter(
      (event) => !event.eventId || !allPlanActionIds.has(event.eventId)
    );
    const overridingEventIds = getOverridingEventIds(visibleEvents, temporalSuggestedEvents);
    const visibleCalendarEvents = visibleEvents.filter(
      (event) => !overridingEventIds.has(event.eventId)
    );
    const groupedEvents = [
      ...visibleCalendarEvents,
      ...transformedAIScheduledEvents.filter(
        (e) =>
          !visibleCalendarEvents.some(
            (ce) =>
              ce.scheduleFrom === 'aiScheduler' &&
              ce.autoScheduleFrom?.id === e.autoScheduleFrom?.id
          )
      ),
      ...transformedActivityEvents.filter(
        (e) =>
          !visibleCalendarEvents.some(
            (ce) => ce.activityFrom?.activityId === e.activityFrom?.activityId
          )
      ), // Filter out exercises that are already in the calendar
      ...transformedAllPlanEvents,
      ...chatEventsWithoutAllPlanDupes,
    ];
    if (creatingEvent) {
      return [...groupedEvents, transformCreatingEventToRBCEvent(creatingEvent)];
    }
    return groupedEvents;
  }, [
    creatingEvent,
    aiScheduledActionsEvents,
    events,
    activityEvents,
    mainCalendar,
    suggestAllAIPlanState,
    temporalSuggestedEvents,
  ]);

    useEffect(() => {
      const calendarContainer = document.querySelector('.rbc-time-content');
      if (calendarContainer) {
        const now = new Date();
        const hours = now.getHours();
        const slotHeight = 17.5;
        const step = CALENDAR_STEP_MIN;
        const slotsPerHour = 60 / step;
        const targetHour = Math.max(0, hours - SCROLL_OFFSET_HOURS);
        const targetSlot = targetHour * slotsPerHour;
        const yOffset = targetSlot * slotHeight;
        calendarContainer.scrollTop = yOffset;
      }
    }, []);

    const handleDropEventFromOutside = useCallback(
      (data: DragFromOutsideItemArgs) => {
        handleDropEventFromOutsideApi(data);

        // Defer the forced remount until after the HTML5 drop lifecycle finishes
        setRerenderTrigger((prev) => prev + 1); // Force re-rendering calendar after dragging & dropping, so it enables to resize right away TODO: asking it on the community (https://github.com/jquense/react-big-calendar/issues/2678)
      },
      [handleDropEventFromOutsideApi]
    );

    useEffect(() => {
      const calendarContainer = document.querySelector('.rbc-time-content');
      if (calendarContainer) {
        const handleScroll = () => {
          currentScrollPosition.current = calendarContainer.scrollTop;
        };

        calendarContainer.addEventListener('scroll', handleScroll);
        return () => calendarContainer.removeEventListener('scroll', handleScroll);
      }
    }, [rerenderTrigger]);

    useEffect(() => {
      if (rerenderTrigger > 0) {
        requestAnimationFrame(() => {
          const calendarContainer = document.querySelector('.rbc-time-content');
          if (calendarContainer) {
            calendarContainer.scrollTop = currentScrollPosition.current;
            currentScrollPosition.current = calendarContainer.scrollTop;
          }
        });
      }
    }, [rerenderTrigger]);

    return (
      <>
        <MemoizedCalendar
          key={rerenderTrigger.toString()}
          ref={dropTaskOnCalendar}
          events={eventWithCreatingOne}
          onEventDrop={onEventDrop}
          onEventResize={onEventResize}
          onSelectEvent={handleSelectEvent}
          onDropEventFromOutside={handleDropEventFromOutside}
          onSelectSlot={({ start, end }) => {
            handleSelectNewEvent({ start: start.toISOString(), end: end.toISOString() });
          }}
          defaultDraggedEvent={defaultDraggedEvent}
          onRemoveTemporalSuggestedEvent={removeTemporalSuggestedEvent}
          className={className}
          date={date}
          minTime={minTime}
          maxTime={maxTime}
          scrollToTime={scrollToTime}
        />
        <TemporaryEventMenu
          visible={!!selectedEvent && visibleMenuMode === 'temporary'}
          onClose={clearSelectedEvent}
        />
        <EditEventMenu
          visible={!!selectedEvent && visibleMenuMode === 'edit'}
          onClose={clearSelectedEvent}
        />
        {selectedActivityEvent?.activityType === 'exercise' ? (
          <AISuggestedEventModal
            visible={visibleMenuMode === 'activity' && !!selectedActivityEvent}
            sessionType="workout"
            name={selectedActivityEvent.summary ?? ''}
            startTime={selectedActivityEvent.start.date}
            endTime={selectedActivityEvent.end.date}
            description=""
            canAcceptCalendar
            canReject
            richContext={buildWorkoutSummaryRichContextFromActivityEvent(selectedActivityEvent)}
            onAskAI={() => undefined}
            onClose={() => {
              clearSelectedEvent();
              clearSelectedActivityEvent();
            }}
            onAcceptCalendar={(calendarId, startTime, endTime) => {
              handleSaveActivityEvent(calendarId, dayjs(startTime), dayjs(endTime));
              clearSelectedEvent();
            }}
            onReject={() => {
              handleDiscardActivityEvent();
              clearSelectedEvent();
            }}
          />
        ) : selectedActivityEvent?.activityType === 'nap' ? (
          <AISuggestedEventModal
            visible={visibleMenuMode === 'activity' && !!selectedActivityEvent}
            sessionType="sleep"
            name={selectedActivityEvent.summary ?? ''}
            startTime={selectedActivityEvent.start.date}
            endTime={selectedActivityEvent.end.date}
            description=""
            canAcceptCalendar
            canReject
            hasNoSleepData={false}
            richContext={buildNapSummaryRichContextFromActivityEvent(selectedActivityEvent)}
            onAskAI={() => undefined}
            onClose={() => {
              clearSelectedEvent();
              clearSelectedActivityEvent();
            }}
            onAcceptCalendar={(calendarId, startTime, endTime) => {
              handleSaveActivityEvent(calendarId, dayjs(startTime), dayjs(endTime));
              clearSelectedEvent();
            }}
            onReject={() => {
              handleDiscardActivityEvent();
              clearSelectedEvent();
            }}
          />
        ) : (
          <ActivitySaveMenu
            title={t('calendar.eventMenu.saveActivity')}
            visible={visibleMenuMode === 'activity' && !!selectedActivityEvent}
            subChildren={
              selectedActivityEvent?.source ? (
                <div style={{ color: grayBase, fontSize: 12, textAlign: 'center' }}>
                  {t('calendar.eventMenu.source', { source: selectedActivityEvent.source })}
                </div>
              ) : null
            }
            defaultStartTime={selectedActivityEvent ? dayjs(selectedActivityEvent.start.date) : null}
            defaultEndTime={selectedActivityEvent ? dayjs(selectedActivityEvent.end.date) : null}
            onClose={() => {
              clearSelectedEvent();
              clearSelectedActivityEvent();
            }}
            onAccept={(_, calendarId, startTime, endTime) =>
              handleSaveActivityEvent(calendarId, startTime, endTime)
            }
            onDiscard={handleDiscardActivityEvent}
          />
        )}
        <AISchedulerFeedbackDialog
          visible={aiFeedbackDialogVisible}
          onDiscard={handleConfirmDiscard}
          onClose={handleFeedbackDialogClose}
        />
      </>
    );
  }
);
